import styled from "@emotion/styled";
import { Box, Stack, Typography } from "@mui/material";
import { ReactComponent as DebtIcon } from "Components/_Shared/Icons/Assets/debt.svg";
import { ReactComponent as FinancialSuccessIcon } from "Components/_Shared/Icons/Assets/financial-success.svg";
import { ReactComponent as HealthIcon } from "Components/_Shared/Icons/Assets/health.svg";
import { ReactComponent as HomeIcon } from "Components/_Shared/Icons/Assets/home.svg";
import { ReactComponent as MoneyBoxIcon } from "Components/_Shared/Icons/Assets/money-box.svg";
import { ReactComponent as ProtectIcon } from "Components/_Shared/Icons/Assets/protect.svg";
import { ReactComponent as TargetIcon } from "Components/_Shared/Icons/Assets/target.svg";
import { ExpenseLineExpenseType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";
import type { FC } from "react";
import { EXPENSE_TYPE_TRANSLATION_KEY } from "../expenseTypes";

type Props = {
  active?: boolean;
  expenseType: ExpenseLineExpenseType;
  onExpenseSelect: (expense: ExpenseLineExpenseType) => void;
  disabled?: boolean;
};

const StyledWrapper = styled(Box, {
  shouldForwardProp: isPropValid,
})<{ $active: boolean; $disabled: boolean }>`
  &:hover {
    cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  }

  ${({ theme, $active }) => `
    box-sizing: border-box;
    height: ${theme.spacing(16)};
    border: ${$active ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.colors.borderLight}`};
    border-radius: ${theme.spacing(Radius.M)};
    padding: ${$active ? theme.spacing(2.8) : theme.spacing(Spacing.L)};
    box-shadow: ${theme.shadows[1]};
    background: ${$active ? theme.palette.primary.light : theme.colors.white};

    svg {
      color: ${$active ? theme.palette.text.primary : theme.palette.text.secondary}
    }
  `}
`;

const getIcon = (expenseType: ExpenseLineExpenseType) => {
  switch (expenseType) {
    case ExpenseLineExpenseType.housing:
      return <HomeIcon />;
    case ExpenseLineExpenseType.consumption:
      return <FinancialSuccessIcon />;
    case ExpenseLineExpenseType.life_style:
      return <HealthIcon />;
    case ExpenseLineExpenseType.loan:
      return <DebtIcon />;
    case ExpenseLineExpenseType["financial-product"]:
      return <MoneyBoxIcon />;
    case ExpenseLineExpenseType.insurance:
      return <ProtectIcon />;
    case ExpenseLineExpenseType.other:
      return <TargetIcon />;
  }
};

export const ExpenseTypeTile: FC<Props> = ({ active = false, expenseType, onExpenseSelect, disabled = false }) => {
  const { t } = useResource();

  return (
    <StyledWrapper $active={active} $disabled={disabled} onClick={() => !disabled && onExpenseSelect(expenseType)}>
      <Stack spacing={Spacing.M}>
        {getIcon(expenseType)}

        <Typography variant="caption" color={active ? undefined : "secondary"}>
          {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes[EXPENSE_TYPE_TRANSLATION_KEY[expenseType]])}
        </Typography>
      </Stack>
    </StyledWrapper>
  );
};
